import type { ReactNode } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

type CategoryStatsStripProps = {
  totalProducts: number;
  categoriesCount: number;
  categorizedCount: number;
  topCategory: { name: string; count: number } | null;
  uncategorizedCount: number;
  /** 2-col compact tiles for mobile, auto-fit row for desktop. */
  variant?: "desktop" | "mobile";
};

/**
 * Four catalog stat tiles — all derived from real product/category data:
 * total products, categorized count, the busiest category, and how many
 * products still need grouping.
 */
export function CategoryStatsStrip({
  totalProducts,
  categoriesCount,
  categorizedCount,
  topCategory,
  uncategorizedCount,
  variant = "desktop",
}: CategoryStatsStripProps) {
  const t = useTranslations();
  const uncategorizedWarn = uncategorizedCount > 0;
  const hasTop = topCategory !== null && topCategory.count > 0;

  const tiles: StatTileProps[] = [
    {
      icon: <BoxIcon />,
      label: t(keys.categories.statTotalProducts),
      value: totalProducts,
      delta: t(keys.categories.statCategoriesSub, { count: categoriesCount }),
      deltaColor: "var(--text-muted)",
    },
    {
      icon: <CheckIcon />,
      label: t(keys.categories.statCategorized),
      value: categorizedCount,
      delta: t(keys.categories.statAcrossGroups),
      deltaColor: "var(--text-muted)",
    },
    {
      icon: <StarIcon />,
      label: t(keys.categories.statTopCategory),
      value: hasTop ? topCategory.name : "—",
      delta: hasTop
        ? t(keys.categories.statProductsSub, { count: topCategory.count })
        : t(keys.categories.statNoProducts),
      deltaColor: "var(--text-muted)",
    },
    {
      icon: <AlertIcon />,
      label: t(keys.categories.statUncategorized),
      value: uncategorizedCount,
      delta: uncategorizedWarn
        ? t(keys.categories.statNeedsGrouping)
        : t(keys.categories.statAllSorted),
      deltaColor: uncategorizedWarn ? "var(--warning-500)" : "var(--text-muted)",
      valueColor: uncategorizedWarn ? "var(--warning-500)" : undefined,
    },
  ];

  if (variant === "mobile") {
    return (
      <div className="grid grid-cols-2 gap-2.5">
        {tiles.map((tile) => (
          <MobileStatTile key={tile.label} {...tile} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} />
      ))}
    </div>
  );
}

type StatTileProps = {
  icon: ReactNode;
  label: string;
  value: string | number;
  delta?: string;
  deltaColor?: string;
  valueColor?: string;
};

function StatTile({ icon, label, value, delta, deltaColor, valueColor }: StatTileProps) {
  return (
    <div className="min-w-0 rounded-[16px] border border-[color:var(--line2)] bg-[color:var(--card)] p-[7px]">
      <div className="rounded-[12px] border border-[color:var(--line2)] bg-[color:var(--surface-alt)] px-[14px] py-[13px]">
        <div className="mb-[11px] flex items-center gap-[9px]">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] border border-[color:var(--border-light)] bg-[color:var(--card)] text-[color:var(--text-primary)]">
            {icon}
          </span>
          <span className="truncate text-[12.5px] font-medium text-[color:var(--text-muted)]">
            {label}
          </span>
        </div>
        <div
          className="truncate text-[27px] font-bold leading-none tracking-[-0.015em]"
          style={{ color: valueColor ?? "var(--text-primary)" }}
          title={String(value)}
        >
          {value}
        </div>
      </div>
      <div className="flex items-center gap-[7px] px-2 pb-[6px] pt-[10px]">
        <span
          aria-hidden="true"
          className="h-[13px] w-[13px] shrink-0 rounded-full border-[1.5px] border-dashed border-[color:var(--border-strong)]"
        />
        <span
          className="truncate text-[11.5px] font-semibold"
          style={{ color: deltaColor ?? "var(--text-muted)" }}
        >
          {delta}
        </span>
      </div>
    </div>
  );
}

function MobileStatTile({ icon, label, value, delta, deltaColor, valueColor }: StatTileProps) {
  return (
    <div className="rounded-[15px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-[13px]">
      <div className="mb-[9px] flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-primary)]">
          {icon}
        </span>
        <span className="truncate text-[11.5px] font-medium text-[color:var(--text-muted)]">
          {label}
        </span>
      </div>
      <div
        className="truncate text-[24px] font-bold leading-none tracking-[-0.015em]"
        style={{ color: valueColor ?? "var(--text-primary)" }}
        title={String(value)}
      >
        {value}
      </div>
      <div className="mt-1.5">
        <span
          className="text-[11px] font-semibold"
          style={{ color: deltaColor ?? "var(--text-muted)" }}
        >
          {delta}
        </span>
      </div>
    </div>
  );
}

function BoxIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 2 2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.3 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6L12 2Z" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}
